import { create } from 'zustand'
import api, { setMemoryToken, clearMemoryToken } from '../lib/api'
import { setUser as sentrySetUser } from '../lib/sentry'
// 2026-06-14 보안 fix: 로그인/로그아웃 시 React Query 캐시 청소 (cross-user 데이터 leak 차단)
import { queryClient } from '../lib/queryClient'

// 스토어 생성 시점에 즉시 localStorage에서 사용자 정보 복원 (새로고침 시 깜빡임 방지)
// 토큰은 httpOnly 쿠키로 관리 → localStorage에 저장하지 않음
const getInitialState = () => {
  try {
    const stored = localStorage.getItem('cams-auth')
    if (stored) {
      const { user } = JSON.parse(stored)
      if (user) {
        return { user, isAuthenticated: true }
      }
    }
  } catch (e) {
    localStorage.removeItem('cams-auth')
  }
  return { user: null, isAuthenticated: false }
}

const initial = getInitialState()

export const useAuthStore = create((set) => ({
  user: initial.user,
  isAuthenticated: initial.isAuthenticated,
  loading: false,
  error: null,

  // 백엔드 API를 통한 로그인 (토큰은 httpOnly 쿠키로 자동 설정)
  login: async (username, password) => {
    set({ loading: true, error: null })
    try {
      const res = await api.post('/auth/login', { username, password })

      const { user, token, requires_role_choice, available_roles } = res.data.data
      if (token) setMemoryToken(token)

      // user_type을 role로 매핑
      const mappedUser = {
        ...user,
        role: user.user_type || user.role
      }

      // 2026-06-14 보안 fix: 신규 로그인 전 캐시 청소 — 이전 세션 데이터 leak 차단
      try { queryClient.clear() } catch (_) {}

      // 2026-10-13: active_role 도 저장한다. 겸업 협력사(capabilities=[plant,maker])가
      //   '제작처' 로 전환해도 새로고침·직접 URL 진입 시 메모리 state 가 사라져
      //   user_type('plant') 으로 되돌아갔고, 라우트 역할 가드가 /auth/me 응답보다
      //   먼저 평가돼 제작처 전용 화면(QR 조회/출력 등)이 홈으로 튕겼다.
      //   역할 문자열이라 민감정보가 아니며, 서버는 JWT 의 active_role 로 최종 판정한다.
      localStorage.setItem('cams-auth', JSON.stringify({
        user: {
          id: mappedUser.id, name: mappedUser.name, role: mappedUser.role,
          user_type: mappedUser.user_type, active_role: mappedUser.active_role ?? null,
        }
      }))
      set({ user: mappedUser, isAuthenticated: true, loading: false, error: null })

      // Sentry 사용자 컨텍스트 부착 (Phase K2-4, PII 제외)
      sentrySetUser(mappedUser)

      return {
        success: true,
        user: mappedUser,
        requires_role_choice: !!requires_role_choice,
        available_roles: available_roles || null,
      }
    } catch (err) {
      const message = err?.response?.data?.error?.message || err?.message || '로그인 중 오류가 발생했습니다.'
      set({ loading: false, error: message, isAuthenticated: false })
      return { success: false, error: message }
    }
  },

  // 외부에서 이미 받은 user로 인증 상태 직접 세팅 (QR 로그인 등)
  setAuth: (user) => {
    // 2026-06-14 보안 fix: 신규 user 세팅 전 캐시 청소
    try { queryClient.clear() } catch (_) {}
    const mappedUser = { ...user, role: user.user_type || user.role }
    localStorage.setItem('cams-auth', JSON.stringify({
      user: {
        id: mappedUser.id, name: mappedUser.name, role: mappedUser.role,
        user_type: mappedUser.user_type, active_role: mappedUser.active_role ?? null,
      }
    }))
    set({ user: mappedUser, isAuthenticated: true, loading: false, error: null })
    sentrySetUser(mappedUser)
  },

  logout: async () => {
    try {
      // 서버에 쿠키 삭제 요청
      await api.post('/auth/logout')
    } catch (e) {
      // 서버 오류에도 로컬 상태는 반드시 초기화
    }
    clearMemoryToken()
    set({ user: null, isAuthenticated: false, error: null })
    localStorage.removeItem('cams-auth')
    sentrySetUser(null)
    // 2026-06-14 보안 fix: logout 시 모든 React Query 캐시 청소 (다음 로그인 사용자 보호)
    try { queryClient.clear() } catch (_) {}
  },

  updateUser: (userData) => {
    set((state) => ({ user: { ...state.user, ...userData } }))
  },

  // 양쪽 capability 협력사: 역할 선택/전환
  selectRole: async (role) => {
    try {
      const res = await api.post('/auth/select-role', { role })
      const newToken = res.data?.data?.token
      if (newToken) setMemoryToken(newToken)
      set((state) => {
        const next = { ...state.user, active_role: role }
        // 새로고침 후에도 전환한 역할이 유지되도록 저장분도 갱신한다
        try {
          const stored = JSON.parse(localStorage.getItem('cams-auth') || '{}')
          if (stored?.user) {
            stored.user.active_role = role
            localStorage.setItem('cams-auth', JSON.stringify(stored))
          }
        } catch (_) { /* 저장 실패해도 메모리 state 로 동작 — /auth/me 가 복원한다 */ }
        return { user: next }
      })
      return { success: true }
    } catch (err) {
      const message = err?.response?.data?.error?.message || err?.message || '역할 전환 실패'
      return { success: false, error: message }
    }
  },

  // Initialize from localStorage (최소 정보로 인증 상태 복원)
  // 페이지 새로고침 시 localStorage의 최소 정보로 먼저 표시하고,
  // /auth/me API로 풀 사용자 정보를 비동기 복원
  initialize: () => {
    const stored = localStorage.getItem('cams-auth')
    if (stored) {
      try {
        const { user } = JSON.parse(stored)
        if (user) {
          set({ user, isAuthenticated: true })
          sentrySetUser(user)
          // Phase JZ-15: 풀 정보 비동기 복원 — memoryToken sessionStorage 복원 의존 (api.js)
          api.get('/auth/me').then(res => {
            if (res.data?.success && res.data?.data?.user) {
              const fullUser = { ...res.data.data.user, role: res.data.data.user.user_type }
              set({ user: fullUser })
              // 2026-10-13: 저장분에 active_role 이 없던 기존 세션도 여기서 메운다.
              //   서버가 JWT 의 active_role 을 돌려주므로(authController /me), 한 번
              //   새로고침하면 다음부터는 가드가 /auth/me 를 기다리지 않아도 역할을 안다.
              try {
                const prev = JSON.parse(localStorage.getItem('cams-auth') || '{}')
                if (prev?.user && prev.user.active_role !== (fullUser.active_role ?? null)) {
                  prev.user.active_role = fullUser.active_role ?? null
                  localStorage.setItem('cams-auth', JSON.stringify(prev))
                }
              } catch (_) { /* 저장 실패해도 메모리 state 로 동작한다 */ }
              sentrySetUser(fullUser)
            }
          }).catch((err) => {
            // 401: token 만료 또는 cross-origin 쿠키 차단 + sessionStorage 미보유
            if (err?.response?.status === 401) {
              clearMemoryToken()
              localStorage.removeItem('cams-auth')
              set({ user: null, isAuthenticated: false })
              sentrySetUser(null)
              // 2026-06-14 보안 fix: 인증 실패 시도 캐시 청소
              try { queryClient.clear() } catch (_) {}
              // hard redirect 는 api.js 401 인터셉터가 처리
            }
            // 그 외 네트워크 에러는 최소 정보 유지
          })
        }
      } catch (e) {
        localStorage.removeItem('cams-auth')
      }
    }
  }
}))
